import type { FC } from "react";
import { useEffect } from "react";
import { Button } from "react-bootstrap";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getTopicAssignments, setTopicAssignments } from "store/slices/assignmentNavigation";

import { useLazyGetAssignmentsInThemeQuery } from "api/generated";

import { processApiError } from "utils/processApiError";

import IconArrowLeft from "images/icons/arrow-left.svg";
import IconArrowRight from "images/icons/arrow-right.svg";

interface Props {
  userAssignmentId: string;
  themeId: string;
  onNavigate: (assignmentId: string) => void;
}

export const TopicAssignmentsNavigation: FC<Props> = ({ userAssignmentId, themeId, onNavigate }) => {
  const assignmentNavigation = useAppSelector(getTopicAssignments(themeId));
  const dispatch = useAppDispatch();

  const [getAssignmentsData, { error: apiLazyError }] = useLazyGetAssignmentsInThemeQuery();

  useEffect(() => {
    if (apiLazyError === undefined) return;

    processApiError(apiLazyError);
    dispatch(setTopicAssignments({ topicId: themeId, assignments: [] }));
  }, [apiLazyError]);

  useEffect(() => {
    if (assignmentNavigation === undefined) {
      getAssignmentsData({ themeId })
        .then(response => {
          const assignments = response.data?.userAssignmentsCached.items.map(assignment => assignment.id) ?? [];
          dispatch(setTopicAssignments({ topicId: themeId, assignments }));
        })
        .catch(error => {
          console.error(error);
          dispatch(setTopicAssignments({ topicId: themeId, assignments: [] }));
        });
    }
  }, [assignmentNavigation]);

  const navigatePrevious = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index === 0) {
      onNavigate(assignmentNavigation[assignmentNavigation.length - 1]);
      return;
    }

    onNavigate(assignmentNavigation[index - 1]);
  };

  const navigateNext = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index >= assignmentNavigation.length - 1) {
      onNavigate(assignmentNavigation[0]);
      return;
    }

    onNavigate(assignmentNavigation[index + 1]);
  };

  return (
    <div className="assignment-navigation">
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigatePrevious}>
        <IconArrowLeft />
      </Button>
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigateNext}>
        <IconArrowRight />
      </Button>
    </div>
  );
};
